/**
 * TREATX MFA session finaliser (client-side).
 * ============================================
 *
 * Both `MfaEnrollPage` and `MfaChallengePage` receive a response body
 * whose shape is IDENTICAL to `POST /api/auth/unified/login` (built by
 * `UnifiedAuthService._finalise_session_after_mfa`). The backend sets
 * the HttpOnly `treatx_auth` + readable `treatx_csrf` cookies on that
 * same response. The frontend must persist the canonical client-side
 * snapshot (`treatx_unified_user` + `treatx_rbac_*`) that AuthContext,
 * RBACContext and SoftLaunchGate read on boot — otherwise the next
 * navigation lands on the public soft-launch page even though the
 * cookies are set (root cause of the "post-MFA-enroll lands on Soft
 * Launch landing" production defect).
 *
 * We deliberately mirror the exact keys and workspace-resolver logic
 * from `UnifiedLoginPage.applyLoginResult` — no parallel client-side
 * SSOT is introduced. See `UnifiedLoginPage.jsx` L546-681.
 */

import {
  resolveUnambiguousWorkspace,
  selectableWorkspaces,
  readTrustedWorkspaceSelection,
  persistTrustedWorkspaceSelection,
} from './workspaceSelection';

const UNIFIED_USER_KEY = 'treatx_unified_user';
const PERMISSIONS_KEY = 'treatx_rbac_permissions';
const ROLE_ASSIGNMENTS_KEY = 'treatx_rbac_role_assignments';
const WORKSPACES_KEY = 'treatx_rbac_workspaces';

/**
 * Persist the canonical session snapshot from an MFA finalise
 * response and resolve the canonical destination. Returns a string
 * hash-route destination that the caller should `window.location.hash`
 * into. Falls back to `data.redirect_path`, then `/`.
 *
 * Side effects (in order):
 *   1. Purge legacy mirrors that would otherwise re-hydrate stale
 *      identity into RBACContext / ClinicDataContext.
 *   2. Write `treatx_unified_user` — read by AuthContext on mount to
 *      set `isAuthenticated`.
 *   3. Write RBAC permissions / role_assignments / workspaces — read
 *      by RBACContext on mount.
 *   4. Set clinic-scoped provider mirrors (matches the
 *      `provider_*` branch in `applyLoginResult`).
 *   5. Broadcast `treatx:identity-changed` so already-mounted
 *      components (PendingInvitationsBanner, workspace switcher,
 *      RBACContext, ClinicDataContext) refresh their in-memory state.
 */
export function persistCanonicalSessionFromMfa(data) {
  if (!data || !data.user || !data.user.email) {
    return '/';
  }

  // Purge legacy mirrors (defence-in-depth — matches applyLoginResult).
  try { localStorage.removeItem('treatx_user'); } catch { /* noop */ }
  try { localStorage.removeItem('treatx_admin'); } catch { /* noop */ }
  try { localStorage.removeItem('treatx_coordinator_token'); } catch { /* noop */ }
  try { localStorage.removeItem('treatx_coordinator_user'); } catch { /* noop */ }
  try { localStorage.removeItem('clinicRepresentative'); } catch { /* noop */ }
  try { sessionStorage.removeItem('treatx_active_clinic_id'); } catch { /* noop */ }
  try { sessionStorage.removeItem('treatx_clinic_user'); } catch { /* noop */ }

  // Canonical unified-user snapshot (AuthContext reads this on mount).
  try { localStorage.setItem(UNIFIED_USER_KEY, JSON.stringify(data.user)); } catch { /* noop */ }

  // RBAC context surfaces.
  try { localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(data.permissions || [])); } catch { /* noop */ }
  try { localStorage.setItem(ROLE_ASSIGNMENTS_KEY, JSON.stringify(data.role_assignments || [])); } catch { /* noop */ }
  try { localStorage.setItem(WORKSPACES_KEY, JSON.stringify(data.workspaces || [])); } catch { /* noop */ }

  // Provider workspace mirror (matches applyLoginResult provider branch).
  const providerRoles = ['provider_admin', 'provider_sales_rep', 'provider_doctor',
                         'provider_after_sales_rep', 'provider_coordinator'];
  if (providerRoles.includes(data.role) && data.clinic_id) {
    try {
      sessionStorage.setItem('treatx_clinic_user', JSON.stringify({
        ...data.user,
        clinicId: data.clinic_id,
        clinicName: data.user.clinic_name || data.clinic_name || '',
        role: data.role,
      }));
      sessionStorage.setItem('treatx_active_clinic_id', data.clinic_id);
    } catch { /* noop */ }
  }

  // Broadcast identity change so mounted contexts pick up the new user.
  try { window.dispatchEvent(new Event('treatx:identity-changed')); } catch { /* noop */ }

  // Canonical destination resolver — copies the exact logic used by
  // `UnifiedLoginPage.applyLoginResult` so post-MFA and post-login land
  // on the same route.
  //
  // TREATX-UAT-PROD-CONTEXT-SURFACE-01c — automatic selection uses the
  // ONE shared rule (`utils/workspaceSelection`). Previously a
  // single-entry catalogue (and, worse, a `patient`/first-entry guess)
  // was written to `treatx_last_active_workspace`, which RBACContext's
  // bootstrap then restores as if it were an EXPLICIT user selection —
  // i.e. a silent context choice for a mixed-persona account. The key is
  // now written only when the sole selectable context is proven.
  const wss = selectableWorkspaces(data.workspaces);
  const unambiguousWs = resolveUnambiguousWorkspace(
    data.role_assignments || [], data.workspaces || []
  );
  let destination = null;
  if (unambiguousWs) {
    destination = unambiguousWs.default_route;
    persistTrustedWorkspaceSelection(unambiguousWs.workspace_id);
  } else if (wss.length >= 1) {
    // AMBIGUOUS — only an EXPLICIT previous selection may be restored.
    // No patient/first-workspace inference, and nothing is persisted.
    let selected = null;
    try {
      const lastId = readTrustedWorkspaceSelection();
      if (lastId) selected = wss.find((w) => w.workspace_id === lastId) || null;
    } catch { /* noop */ }
    if (selected) destination = selected.default_route;
  }
  if (!destination) destination = data.redirect_path || '/';
  return destination;
}
